/* prettier-ignore-file */
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  TextInput,
  Modal,
  Animated,
} from "react-native";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase.config";
import AuthHeader from "../components/AuthHeader";
import BookingAlertsStrip from "../components/BookingAlertsStrip";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { getCollectionPath } from "../utils/dbPaths";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase.config";
import { onAuthStateChanged } from "firebase/auth";
import { useBookings, useProducts, useSettings, useAuthStore, useCompletedBookings, useStripeTestMode } from "../stores";
import { capitalizeWords, formatPhoneNumber, parseFirestoreTimestamp } from "../utils/formatting";
import { getPickupOptionsText, logs } from "../utils/helperFuns";
import ContactOptionsModal from "../utils/ContactOptionsModal";

const functions = getFunctions(app);

const Admin = () => {
  const navigate = useNavigate();
  
  // Contact modal state
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Blinking animation for breakdown indicator
  const breakdownBlinkOpacity = useRef(new Animated.Value(1)).current;
  
  // Blinking animation for paid amount when not > 0
  const paidBlinkOpacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Start blinking animation: on for 0.5s, off for 0.5s
    const blinkAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(breakdownBlinkOpacity, {
          toValue: 0,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(breakdownBlinkOpacity, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
      ])
    );
    blinkAnimation.start();

    return () => {
      blinkAnimation.stop();
    };
  }, [breakdownBlinkOpacity]);
  
  useEffect(() => {
    // Start blinking animation for paid amount: on for 0.5s, off for 0.5s
    const paidBlinkAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(paidBlinkOpacity, {
          toValue: 0,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(paidBlinkOpacity, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
      ])
    );
    paidBlinkAnimation.start();

    return () => {
      paidBlinkAnimation.stop();
    };
  }, [paidBlinkOpacity]);

  // Subscribe to Zustand bookings store for live updates
  const bookingsFromStore = useBookings(state => state.bookings);
  
  // Helper: Get booking card info (type, color, border) - shows combination of services
  const getBookingCardInfo = (booking) => {
    const hasDelivery = booking.delivery || false;
    const hasHomePickup = booking.homePickup || false;
    const hasInStore = booking.inStorePickup || false;

    // Build type text based on combinations
    let type = "";
    let color = "#8A9794";
    let borderColor = "#8A9794";

    // Delivery + HomePickup
    if (hasDelivery && hasHomePickup) {
      type = "Delivery & Home Pickup";
      color = "#C4453D"; // Red for delivery
      borderColor = "#C4453D";
    }
    // Delivery + InStore
    else if (hasDelivery && hasInStore) {
      type = "Delivery & In-Store Dropoff";
      color = "#C4453D"; // Red for delivery
      borderColor = "#C4453D";
    }
    // HomePickup + InStore
    else if (hasHomePickup && hasInStore) {
      type = "In-Store Pickup & Home Pickup";
      color = "#B4761B"; // Yellow for in-store
      borderColor = "#B4761B";
    }
    // Only Delivery
    else if (hasDelivery) {
      type = "Delivery";
      color = "#C4453D"; // Red
      borderColor = "#C4453D";
    }
    // Only HomePickup
    else if (hasHomePickup) {
      type = "Home Pickup";
      color = "#1F8A5B"; // Green
      borderColor = "#1F8A5B";
    }
    // Only InStore
    else if (hasInStore) {
      type = "In-Store Pickup & Dropoff";
      color = "#B4761B"; // Yellow
      borderColor = "#B4761B";
    }
    // Default fallback (no delivery/pickup options selected)
    else {
      type = "Rental";
      color = "#8A9794";
      borderColor = "#8A9794";
    }

    return { type, color, borderColor };
  };
  
  // Helper: Format date like ViewBooking (e.g., "Mon, Jan 15, 2025")
  const formatDateLong = (date) => {
    if (!date) return "N/A";

    let dateObj;
    if (date.toDate) {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date.seconds) {
      dateObj = new Date(date.seconds * 1000);
    } else {
      dateObj = new Date(date);
    }

    return dateObj.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  
  // Auth state
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authChecked = useAuthStore((state) => state.authChecked);
  const userLoading = !authChecked;
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [downloadingCompletedForSearch, setDownloadingCompletedForSearch] = useState(false);
  const [searchLastName, setSearchLastName] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [hasDatabaseSearchRun, setHasDatabaseSearchRun] = useState(false);
  const [listSortMode, setListSortMode] = useState("startEarliest");

  // Tab state - default to "Current"
  const [activeTab, setActiveTab] = useState("Current");
  
  // Bookings
  const [displayedBookings, setDisplayedBookings] = useState([]); // Filtered bookings to display
  
  // Completed bookings store
  const { completedBookings, completedLoaded, setCompletedBookings } = useCompletedBookings();
  const products1FromStore = useProducts((state) => state.products1);
  const products2FromStore = useProducts((state) => state.products2);

  // Product category names
  const [product1CategoryName, setProduct1CategoryName] = useState("Product1");
  const [product2CategoryName, setProduct2CategoryName] = useState("Product2");
  
  // Store info for header
  const [shopInfo, setShopInfo] = useState({});
  const [storeLogo, setStoreLogo] = useState("");
  const [storeName, setStoreName] = useState("");
  
  // Refund/Cancel state
  
  // Loading state for send invoice
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [collectingPayment, setCollectingPayment] = useState(false);
  const [sendingWaiver, setSendingWaiver] = useState(false);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check authentication - handled by store
  useEffect(() => {
    if (authChecked && !isAuthenticated) {
        navigate("/login");
    }
  }, [authChecked, isAuthenticated, navigate]);

  // Load product category names from settings and all bookings
  // Get settings from Zustand store
  const settings = useSettings((state) => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);

  // Update state when settings change
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      const product1Name = settings.product1Title?.title || "Product1";
      const product2Name = settings.product2Title?.title || "Product2";
      const storeNameValue = settings.storeName?.name || "";
      const storeLogoValue = settings.storeLogo?.url || "";
      const storePhoneValue = settings.storePhone?.phone || "";
      const storeEmailValue = settings.storeEmail?.email || "";
      
      setProduct1CategoryName(product1Name);
      setProduct2CategoryName(product2Name);
      setStoreName(storeNameValue);
      setStoreLogo(storeLogoValue);
      setShopInfo({
        name: storeNameValue,
        phone: storePhoneValue,
        email: storeEmailValue,
      });
    }
  }, [settings]);
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery(""); // Clear search when switching tabs
    setSearching(false);
    
    // Search tab keeps its own cloud-search results
    if (tab === "Search") {
      if (!hasDatabaseSearchRun) {
        setDisplayedBookings([]);
      }
    }
  };

  // Helper: Check if booking is in completedBookings collection
  const isBookingInCompletedBookings = (booking) => {
    const bookingId = booking.id || booking.bookingId;
    return completedBookings.some(cb => (cb.id === bookingId || cb.bookingId === bookingId));
  };

  // Helper: Parse booking start/end dates and normalize to start-of-day
  const getBookingDayRange = (booking) => {
    const startMillis = parseFirestoreTimestamp(booking.startDate);
    const endMillis = parseFirestoreTimestamp(booking.endDate);

    if (!startMillis || !endMillis) {
      return { startDayMillis: null, endDayMillis: null };
    }

    const startDate = new Date(startMillis);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(endMillis);
    endDate.setHours(0, 0, 0, 0);

    return {
      startDayMillis: startDate.getTime(),
      endDayMillis: endDate.getTime(),
    };
  };

  const getTodayStartMillis = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime();
  };

  const isUpcomingBooking = (booking) => {
    const { startDayMillis } = getBookingDayRange(booking);
    if (startDayMillis === null) return false;
    return startDayMillis > getTodayStartMillis();
  };

  const isCurrentBooking = (booking) => {
    const { startDayMillis, endDayMillis } = getBookingDayRange(booking);
    if (startDayMillis === null || endDayMillis === null) return false;
    const todayStartMillis = getTodayStartMillis();
    return startDayMillis <= todayStartMillis && endDayMillis >= todayStartMillis;
  };

  const isEndedBooking = (booking) => {
    const { endDayMillis } = getBookingDayRange(booking);
    if (endDayMillis === null) return false;
    return endDayMillis < getTodayStartMillis();
  };

  // Completed tab: ended bookings still in active/open bookings store
  const getCurrentTabBookings = () => {
    return bookingsFromStore.filter(
      (booking) => booking.cancelled !== true && isCurrentBooking(booking)
    );
  };

  const getUpcomingTabBookings = () => {
    return bookingsFromStore.filter(
      (booking) => booking.cancelled !== true && isUpcomingBooking(booking)
    );
  };

  const getCompletedTabBookings = () => {
    return bookingsFromStore.filter(
      (booking) => booking.cancelled !== true && isEndedBooking(booking)
    );
  };

  const currentTabCount = getCurrentTabBookings().length;
  const upcomingTabCount = getUpcomingTabBookings().length;
  const completedTabCount = getCompletedTabBookings().length;

  const activeTabSummary = React.useMemo(() => {
    const product1InventoryNames = new Set(
      (products1FromStore || [])
        .filter((item) => item?.quantity !== null && item?.quantity !== undefined)
        .map((item) => item?.name)
        .filter(Boolean)
    );

    const product2InventoryIds = new Set(
      (products2FromStore || [])
        .filter((item) => item?.inventory !== null && item?.inventory !== undefined)
        .map((item) => item?.id)
        .filter(Boolean)
    );

    const product2InventoryNames = new Set(
      (products2FromStore || [])
        .filter((item) => item?.inventory !== null && item?.inventory !== undefined)
        .map((item) => item?.name)
        .filter(Boolean)
    );

    const product1Totals = {};
    const product2Totals = {};

    (displayedBookings || []).forEach((booking) => {
      const product1Items = Array.isArray(booking?.product1) ? booking.product1 : [];
      product1Items.forEach((item) => {
        const itemName = item?.name;
        if (!itemName) return;

        // Exclude non-inventory items when we have inventory metadata
        if (product1InventoryNames.size > 0 && !product1InventoryNames.has(itemName)) return;

        const qty = Number(item?.quantity || 0);
        if (qty <= 0) return;

        product1Totals[itemName] = (product1Totals[itemName] || 0) + qty;
      });

      const rawProduct2 = booking?.product2;
      const product2Items = Array.isArray(rawProduct2)
        ? rawProduct2
        : (rawProduct2 && typeof rawProduct2 === "object"
          ? Object.entries(rawProduct2).map(([id, value]) => ({ id, ...(value || {}) }))
          : []);

      product2Items.forEach((item) => {
        const itemId = item?.id;
        const itemName = item?.name || itemId;
        if (!itemName) return;

        // Exclude non-inventory items when inventory metadata is available
        if (product2InventoryIds.size > 0 || product2InventoryNames.size > 0) {
          const isInventoryItem =
            (itemId && product2InventoryIds.has(itemId)) || product2InventoryNames.has(itemName);
          if (!isInventoryItem) return;
        }

        const qty = Number(item?.quantity || 0);
        if (qty <= 0) return;

        product2Totals[itemName] = (product2Totals[itemName] || 0) + qty;
      });
    });

    const product1List = Object.entries(product1Totals).sort((a, b) => b[1] - a[1]);
    const product2List = Object.entries(product2Totals).sort((a, b) => b[1] - a[1]);

    const product1TotalCount = product1List.reduce((sum, [, qty]) => sum + qty, 0);
    const product2TotalCount = product2List.reduce((sum, [, qty]) => sum + qty, 0);

    return { product1List, product2List, product1TotalCount, product2TotalCount };
  }, [products1FromStore, products2FromStore, displayedBookings]);

  const formatPhoneWithDashes = (value) => {
    const digits = (value || "").replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const scoreTextField = (rawValue, searchTerm) => {
    if (!searchTerm) return 0;
    const value = (rawValue || "").toLowerCase();
    if (!value) return -1;
    if (value === searchTerm) return 120;
    if (value.startsWith(searchTerm)) return 90;
    const matchIndex = value.indexOf(searchTerm);
    if (matchIndex === -1) return -1;
    return 60 - Math.min(matchIndex, 20);
  };

  const scorePhoneField = (rawValue, searchDigits) => {
    if (!searchDigits) return 0;
    const digits = (rawValue || "").replace(/\D/g, "");
    if (!digits) return -1;
    if (digits === searchDigits) return 120;
    if (digits.startsWith(searchDigits)) return 90;
    const matchIndex = digits.indexOf(searchDigits);
    if (matchIndex === -1) return -1;
    return 60 - Math.min(matchIndex, 20);
  };

  const runCloudDatabaseSearch = async () => {
    const lastNameTerm = searchLastName.trim().toLowerCase();
    const emailTerm = searchEmail.trim().toLowerCase();
    const phoneDigits = searchPhone.replace(/\D/g, "");

    if (!lastNameTerm && !emailTerm && !phoneDigits) {
      alert("Enter at least one search field.");
      return;
    }

    setSearching(true);
    setDownloadingCompletedForSearch(false);

    try {
      const bookingsPath = getCollectionPath("bookings");
      const completedPath = getCollectionPath("completedBookings");

      const [bookingsSnapshot, completedSnapshot] = await Promise.all([
        getDocs(collection(db, bookingsPath)),
        getDocs(collection(db, completedPath)),
      ]);

      const openBookings = [];
      bookingsSnapshot.forEach((docSnap) => {
        openBookings.push({ id: docSnap.id, ...docSnap.data(), __sourceNode: "bookings" });
      });

      const archivedBookings = [];
      completedSnapshot.forEach((docSnap) => {
        archivedBookings.push({
          id: docSnap.id,
          ...docSnap.data(),
          __sourceNode: "completedBookings",
          __fromCompletedArchive: true,
        });
      });

      const combined = [...openBookings, ...archivedBookings];

      const scoredResults = combined
        .map((booking) => {
          let score = 0;

          if (lastNameTerm) {
            const lastNameScore = scoreTextField(booking.lastName, lastNameTerm);
            if (lastNameScore < 0) return null;
            score += lastNameScore;
          }

          if (emailTerm) {
            const emailScore = scoreTextField(booking.customerEmail || booking.email, emailTerm);
            if (emailScore < 0) return null;
            score += emailScore;
          }

          if (phoneDigits) {
            const phoneScore = scorePhoneField(booking.customerPhone || booking.phone, phoneDigits);
            if (phoneScore < 0) return null;
            score += phoneScore;
          }

          if (score <= 0) return null;

          return { booking, score };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          const aTime = a.booking.createdAt?.seconds || 0;
          const bTime = b.booking.createdAt?.seconds || 0;
          return bTime - aTime;
        })
        .map((item) => item.booking);

      setDisplayedBookings(scoredResults);
      setHasDatabaseSearchRun(true);
    } catch (error) {
      console.error("❌ Error searching cloud database:", error);
      alert("Error searching database: " + (error.message || "Unknown error"));
    } finally {
      setSearching(false);
    }
  };

  // Get bookings based on active tab
  const getBookingsForTab = () => {
    if (activeTab === "Current") {
      return getCurrentTabBookings();
    } else if (activeTab === "Upcoming") {
      return getUpcomingTabBookings();
    } else if (activeTab === "Completed") {
      return getCompletedTabBookings();
    } else if (activeTab === "Search") {
      return [];
    }
    return [];
  };

  // Comprehensive search function that searches all fields independently and ranks results
  const performSearch = async (query, currentBookings, completedBookingsData, isCompletedLoaded) => {
    if (!query || query.trim().length < 3) {
      return [];
    }

    setSearching(true);
    const queryTrimmed = query.trim();
    const queryLower = queryTrimmed.toLowerCase();
    const cleanPhone = queryTrimmed.replace(/\D/g, "");

    // Ensure completedBookings is loaded if not already
    let completedBookingsToSearch = completedBookingsData;
    if (!isCompletedLoaded) {
      setDownloadingCompletedForSearch(true);
      try {
        const completedPath = getCollectionPath("completedBookings");
        const completedRef = collection(db, completedPath);
        const snapshot = await getDocs(completedRef);

        const bookings = [];
        snapshot.forEach((doc) => {
          bookings.push({ id: doc.id, ...doc.data() });
        });

        setCompletedBookings(bookings);
        completedBookingsToSearch = bookings;
      } catch (error) {
        console.error("❌ Error loading completed bookings for search:", error);
      } finally {
        setDownloadingCompletedForSearch(false);
      }
    }

    // Search function that scores each booking
    const scoreBooking = (booking) => {
      let score = 0;
      const matches = [];

      // Search first name (independent)
      const firstName = (booking.firstName || "").toLowerCase();
      if (firstName.includes(queryLower)) {
        const matchIndex = firstName.indexOf(queryLower);
        // Exact match gets higher score, earlier match gets higher score
        const matchScore = firstName === queryLower ? 100 : 50 - matchIndex;
        score += matchScore;
        matches.push({ field: "firstName", score: matchScore });
      }

      // Search last name (independent)
      const lastName = (booking.lastName || "").toLowerCase();
      if (lastName.includes(queryLower)) {
        const matchIndex = lastName.indexOf(queryLower);
        const matchScore = lastName === queryLower ? 100 : 50 - matchIndex;
        score += matchScore;
        matches.push({ field: "lastName", score: matchScore });
      }

      // Search booking ID (independent)
      const bookingId = (booking.id || "").toLowerCase();
      const bookingId2 = (booking.bookingId || "").toLowerCase();
      const customerBookingId = (booking.customerBookingID || "").toLowerCase();

      if (bookingId.includes(queryLower)) {
        const matchIndex = bookingId.indexOf(queryLower);
        const matchScore = bookingId === queryLower ? 100 : 50 - matchIndex;
        score += matchScore;
        matches.push({ field: "bookingId", score: matchScore });
      }
      if (bookingId2.includes(queryLower)) {
        const matchIndex = bookingId2.indexOf(queryLower);
        const matchScore = bookingId2 === queryLower ? 100 : 50 - matchIndex;
        score += matchScore;
        matches.push({ field: "bookingId2", score: matchScore });
      }
      if (customerBookingId.includes(queryLower)) {
        const matchIndex = customerBookingId.indexOf(queryLower);
        const matchScore = customerBookingId === queryLower ? 100 : 50 - matchIndex;
        score += matchScore;
        matches.push({ field: "customerBookingId", score: matchScore });
      }

      // Search email (independent)
      const email = (booking.customerEmail || "").toLowerCase();
      if (email.includes(queryLower)) {
        const matchIndex = email.indexOf(queryLower);
        const matchScore = email === queryLower ? 100 : 50 - matchIndex;
        score += matchScore;
        matches.push({ field: "email", score: matchScore });
      }

      // Search phone number (independent, digits only)
      if (cleanPhone.length > 0) {
        const phone = (booking.customerPhone || "").replace(/\D/g, "");
        if (phone.includes(cleanPhone)) {
          const matchIndex = phone.indexOf(cleanPhone);
          const matchScore = phone === cleanPhone ? 100 : 50 - matchIndex;
          score += matchScore;
          matches.push({ field: "phone", score: matchScore });
        }
      }

      return { booking, score, matches };
    };

    // Search current bookings
    const currentResults = (currentBookings || [])
      .map(scoreBooking)
      .filter(result => result.score > 0);

    // Search completed bookings (now guaranteed to be loaded)
    const completedResults = (completedBookingsToSearch || [])
      .map(scoreBooking)
      .filter(result => result.score > 0);

    // Combine and deduplicate by booking ID, keeping highest score
    const allResults = [...currentResults, ...completedResults];
    const resultMap = new Map();

    allResults.forEach(result => {
      const bookingId = result.booking.id || result.booking.bookingId;
      if (!resultMap.has(bookingId) || resultMap.get(bookingId).score < result.score) {
        resultMap.set(bookingId, result);
      }
    });

    // Sort by score (highest first) and return bookings
    const sortedResults = Array.from(resultMap.values())
      .sort((a, b) => b.score - a.score)
      .map(result => result.booking);

    setSearching(false);
    return sortedResults;
  };

  // Update displayed bookings when store changes or tab changes
  useEffect(() => {
    const updateDisplayedBookings = async () => {
      if (activeTab === "Search") {
        if (!hasDatabaseSearchRun) {
          setDisplayedBookings([]);
        }
        setDownloadingCompletedForSearch(false);
        return;
      }

      if (searchQuery.trim().length < 3) {
        const bookingsToDisplay = getBookingsForTab();
        setDisplayedBookings(bookingsToDisplay);
        setSearching(false);
        setDownloadingCompletedForSearch(false);
        return;
      }

      // Perform comprehensive search
      const searchResults = await performSearch(searchQuery, bookingsFromStore, completedBookings, completedLoaded);
      setDisplayedBookings(searchResults);
    };

    updateDisplayedBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, bookingsFromStore, completedBookings, activeTab, completedLoaded, hasDatabaseSearchRun]);

  // Calculate totals for a booking (NEW STRUCTURE: flat booking)
  const calculateBookingTotals = (booking) => {
    let totalProduct1 = 0;
    let totalProduct2 = 0;
    let totalPaid = 0;
    let totalRefunded = 0;
    let isDevBypass = false;
    let hasPaymentData = false;

    // Check if this is a dev bypass booking
    isDevBypass = booking.bookingId?.startsWith("DEV_") || booking.id?.startsWith("DEV_") || booking.isDevBypass === true;

    // NEW STRUCTURE: Use product1 (array) and product2 (array)
    const product1Items = booking.product1 || [];
    totalProduct1 = product1Items.reduce((sum, item) => sum + (item.quantity || 0), 0);

    const product2Items = booking.product2 || [];
    product2Items.forEach(item => {
      totalProduct2 += item.quantity || 0;
    });

    // NEW STRUCTURE: Get payment data from payments array
    // Check if payments array exists and has items
    const paymentsArray = booking.payments;
    if (paymentsArray && Array.isArray(paymentsArray) && paymentsArray.length > 0) {
      // Sum all payments - costBreakdown.totalCharged is in cents
      paymentsArray.forEach(payment => {
        // Prefer costBreakdown.totalCharged (in cents), fallback to amount (may be in cents or dollars)
        const paymentAmount = payment.costBreakdown?.totalCharged || payment.amount || payment.total || payment.totalCharge || 0;
        totalPaid += paymentAmount;
      });
      // Only set hasPaymentData to true if there's actually money paid
      hasPaymentData = totalPaid > 0;
    }
    // If payments array is empty or doesn't exist, hasPaymentData remains false
    // This ensures buttons show and $0.00 is displayed for bookings without payments
    
    // Refunds stored in cents
    if (booking.refunds && Array.isArray(booking.refunds)) {
      booking.refunds.forEach(refund => {
        totalRefunded += Number(refund.amount ?? refund.refundAmount ?? 0) / 100;
      });
    }

    return { 
      totalProduct1, 
      totalProduct2, 
      totalPaid, 
      totalRefunded,
      isDevBypass,
      hasPaymentData,
      totalCost: 0  // Will be read from booking.totalAmount instead
    };
  };

  // Handle edit booking (NEW STRUCTURE: use booking.id directly)
  const handleEditBooking = (booking) => {
    const bookingIdForEdit = booking.id || booking.bookingId;
    navigate(`/edit-booking-wizard/${bookingIdForEdit}`);
  };
  
  const handleViewBooking = (booking) => {
    const bookingIdForView = booking.id || booking.bookingId;
    navigate(`/view-booking/${bookingIdForView}`);
  };

  // Payment collection handlers (same as ViewBooking)
  const handleCollectPayment = async (booking) => {
    const bookingId = booking.id || booking.bookingId;
    if (!booking || !bookingId) {
      alert("Error: Booking information not available");
      return;
    }

    const totalAmountCents = booking.totalAmount || 0;
    if (totalAmountCents <= 0) {
      alert("Error: Invalid payment amount");
      return;
    }

    setCollectingPayment(true);

    try {
      const createPaymentLink = httpsCallable(functions, "createPaymentLink");
      const customerName = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Customer";
      
      const paymentResult = await createPaymentLink({
        amountCents: totalAmountCents,
        description: `Payment for Booking ${bookingId}`,
        customerEmail: booking.customerEmail || "",
        customerPhone: booking.customerPhone || "",
        customerName: customerName,
        saveCard: true,
        bookingId: bookingId,
        isBookingEdit: true, // Mark as edit booking so receipts are sent
        skipBookingConfirmation: true, // Skip booking confirmation, only send receipts
        testMode: useTestStripe,
      });

      if (!paymentResult.data?.url) {
        throw new Error("Failed to create payment link");
      }

      // Navigate to Stripe Checkout
      navigate("/stripe-checkout", {
        state: {
          paymentUrl: paymentResult.data.url,
          bookingId: bookingId,
          referrer: "/rental-bookings", // Store where we came from
        },
      });
    } catch (error) {
      console.error("❌ Error creating payment link:", error);
      setCollectingPayment(false);
      alert("Error: " + (error.message || "Failed to create payment link"));
    }
  };

  const handleSendWaiver = async (booking) => {
    const bookingId = booking.id || booking.bookingId;
    if (!booking || !bookingId) {
      alert("Error: Booking information not available");
      return;
    }

    setSendingWaiver(true);

    try {
      const sendWaiverReminder = httpsCallable(functions, "sendWaiverReminder");
      await sendWaiverReminder({
        bookingId: bookingId,
      });

      alert("✅ Waiver reminder sent successfully!\n\nThe customer will receive an email and SMS reminder to sign the waiver.");
    } catch (error) {
      console.error("❌ Error sending waiver reminder:", error);
      alert("Error: Failed to send waiver reminder - " + (error.message || "Unknown error"));
    } finally {
      setSendingWaiver(false);
    }
  };

  const handleSendTextInvoice = async (booking) => {
    const bookingId = booking.id || booking.bookingId;
    if (!booking || !bookingId) {
      alert("Error: Booking information not available");
      return;
    }

    const totalAmountCents = booking.totalAmount || 0;
    if (totalAmountCents <= 0) {
      alert("Error: Invalid payment amount");
      return;
    }

    if (!booking.customerPhone) {
      alert("Error: Customer phone number not available");
      return;
    }

    setSendingInvoice(true);

    try {
      // Create payment link first
      const createPaymentLink = httpsCallable(functions, "createPaymentLink");
      const customerName = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Customer";
      
      const paymentResult = await createPaymentLink({
        amountCents: totalAmountCents,
        description: `Payment for Booking ${bookingId}`,
        customerEmail: booking.customerEmail || "",
        customerPhone: booking.customerPhone || "",
        customerName: customerName,
        saveCard: true,
        bookingId: bookingId,
        isBookingEdit: true, // Mark as edit booking so receipts are sent
        skipBookingConfirmation: true, // Skip booking confirmation, only send receipts
        testMode: useTestStripe,
      });

      if (!paymentResult.data?.url) {
        throw new Error("Failed to create payment link");
      }

      // Send SMS with payment link
      const sendPaymentLinkSms = httpsCallable(functions, "sendPaymentLinkSms");
      await sendPaymentLinkSms({
        bookingId: bookingId,
        paymentUrl: paymentResult.data.url,
        customerPhone: booking.customerPhone,
        customerName: customerName,
      });

      setSendingInvoice(false);
      alert("Payment link sent successfully!");
    } catch (error) {
      console.error("❌ Error sending payment link:", error);
      setSendingInvoice(false);
      alert("Error: " + (error.message || "Failed to send payment link"));
    }
  };



  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      let d;
      // Handle Firestore Timestamp objects
      if (date.toDate && typeof date.toDate === 'function') {
        d = date.toDate();
      } else if (typeof date === 'object' && date.seconds) {
      // Handle objects with seconds property (Firestore timestamp format)
        d = new Date(date.seconds * 1000);
      } else {
      // Handle regular Date objects or ISO strings
        d = new Date(date);
      }
      
      if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", {
          weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      }
      return "N/A";
    } catch (error) {
      console.error("Error formatting date:", error, date);
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (typeof amount === "number") {
      // Amount is in cents, convert to dollars
      const dollars = amount / 100;
      return `$${dollars.toFixed(2)}`;
    }
    return "$0.00";
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length !== 10) return phone;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  };
  
  // Helper: Check if booking has ended
  const isBookingOverdue = (booking) => {
    return isEndedBooking(booking);
  };
  
  // Helper: Get check-in indicator status for a booking
  const getCheckInIndicator = (booking) => {
    const bookingIntake = booking.bookingIntake;
    
    if (bookingIntake && bookingIntake.underReview === true) {
      return "green"; // Green: saved for review
    } else if (!bookingIntake || bookingIntake.underReview !== true) {
      return "red"; // Red: not saved for review or no check-in
    }
    
    return null;
  };
  
  // Helper: Get date label for booking card header (Yesterday/Today/Tomorrow/Day After Tomorrow)
  const getDateLabel = (booking) => {
    const startMillis = parseFirestoreTimestamp(booking.startDate);
    if (!startMillis) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const bookingDate = new Date(startMillis);
    bookingDate.setHours(0, 0, 0, 0);
    
    const diffDays = Math.floor((bookingDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === -1) {
      return { text: "Yesterday", color: "#B4761B" }; // Yellow
    } else if (diffDays === 0) {
      return { text: "Today", color: "#C4453D" }; // Red
    } else if (diffDays === 1) {
      return { text: "Tomorrow", color: "#0E7C6B" }; // Blue
    } else if (diffDays === 2) {
      return { text: "Day After Tomorrow", color: "#5C6B68" }; // Gray
    }
    
    return null;
  };
  
  // Only split overdue/current on Current tab
  const shouldSplitOverdue = activeTab === "Current";
  const overdueBookings = shouldSplitOverdue
    ? displayedBookings.filter(booking => isBookingOverdue(booking))
    : [];
  const currentBookings = shouldSplitOverdue
    ? displayedBookings.filter(booking => !isBookingOverdue(booking))
    : displayedBookings;
  const activeTabLabel = activeTab;
  const isCloudSearchTab = activeTab === "Search";
  const isLocalSearchActive = !isCloudSearchTab && searchQuery.length >= 3;
  const isCloudSearchActive = isCloudSearchTab && hasDatabaseSearchRun;
  const sortedCurrentBookings = React.useMemo(() => {
    const compareDates = (aTs, bTs, asc = true) => {
      const aValid = Number.isFinite(aTs);
      const bValid = Number.isFinite(bTs);
      if (!aValid && !bValid) return 0;
      if (!aValid) return 1;
      if (!bValid) return -1;
      return asc ? aTs - bTs : bTs - aTs;
    };

    const bookingsToSort = [...currentBookings];
    bookingsToSort.sort((a, b) => {
      const aStart = parseFirestoreTimestamp(a.startDate);
      const bStart = parseFirestoreTimestamp(b.startDate);
      const aEnd = parseFirestoreTimestamp(a.endDate);
      const bEnd = parseFirestoreTimestamp(b.endDate);

      let primary = 0;
      if (listSortMode === "startEarliest") {
        primary = compareDates(aStart, bStart, true);
      } else if (listSortMode === "startLatest") {
        primary = compareDates(aStart, bStart, false);
      } else if (listSortMode === "endEarliest") {
        primary = compareDates(aEnd, bEnd, true);
      } else if (listSortMode === "endLatest") {
        primary = compareDates(aEnd, bEnd, false);
      }

      if (primary !== 0) return primary;

      // Stable fallback for same date values
      const aCreated = a.createdAt?.seconds || 0;
      const bCreated = b.createdAt?.seconds || 0;
      return bCreated - aCreated;
    });

    return bookingsToSort;
  }, [currentBookings, listSortMode]);
  
  // Check if device is mobile
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

  if (userLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0E7C6B" />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  // logs(bookingsFromStore)
  return (
    <View style={styles.container}>
      <AuthHeader 
        title="Rental Bookings" 
        subtitle={storeName ? `by ${storeName}` : ""}
        isDesktop={isDesktop} 
        isAuthenticated={true}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      <View style={isDesktop ? styles.desktopWrapper : null}>
      <ScrollView style={styles.content}>
          {/* Needs-a-human states first: oversold bookings and cash held at the
              register. Renders nothing when there are none. */}
          <BookingAlertsStrip />

          {/* Tab Bar */}
          <View style={styles.tabBar}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.tabButton, activeTab === "Current" && styles.tabButtonActive]}
              onPress={() => handleTabChange("Current")}
            >
              <Text style={[styles.tabButtonText, activeTab === "Current" && styles.tabButtonTextActive]}>
                {`Current (${currentTabCount})`}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.tabButton, activeTab === "Upcoming" && styles.tabButtonActive]}
              onPress={() => handleTabChange("Upcoming")}
            >
              <Text style={[styles.tabButtonText, activeTab === "Upcoming" && styles.tabButtonTextActive]}>
                {`Upcoming (${upcomingTabCount})`}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.tabButton, activeTab === "Completed" && styles.tabButtonActive]}
              onPress={() => handleTabChange("Completed")}
            >
              <Text style={[styles.tabButtonText, activeTab === "Completed" && styles.tabButtonTextActive]}>
                {`Completed (${completedTabCount})`}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.tabButton, activeTab === "Search" && styles.tabButtonActive]}
              onPress={() => handleTabChange("Search")}
            >
              <Text style={[styles.tabButtonText, activeTab === "Search" && styles.tabButtonTextActive]}>
                Search
              </Text>
            </TouchableOpacity>
          </View>

        {/* Inventory Summary for currently selected tab/list */}
        {activeTab !== "Search" && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{activeTab} Summary</Text>
            <View style={styles.summaryContainer}>
              <View style={styles.summaryTabBlock}>
                <Text style={styles.summaryLine}>
                  {product1CategoryName}: {activeTabSummary.product1TotalCount}
                </Text>
                {activeTabSummary.product1List.length > 0 ? (
                  <Text style={styles.summaryItemsText}>
                    {activeTabSummary.product1List.map(([name, qty]) => `${name}: ${qty}`).join(" | ")}
                  </Text>
                ) : (
                  <Text style={styles.summaryItemsText}>No inventory items</Text>
                )}

                <Text style={[styles.summaryLine, { marginTop: 8 }]}>
                  {product2CategoryName}: {activeTabSummary.product2TotalCount}
                </Text>
                {activeTabSummary.product2List.length > 0 ? (
                  <Text style={styles.summaryItemsText}>
                    {activeTabSummary.product2List.map(([name, qty]) => `${name}: ${qty}`).join(" | ")}
                  </Text>
                ) : (
                  <Text style={styles.summaryItemsText}>No inventory items</Text>
                )}
              </View>
            </View>
          </View>
        )}

        {/* Booking Search */}
        {activeTab !== "Search" ? (
          <View style={styles.searchSection}>
            {(searching || downloadingCompletedForSearch) && (
              <View style={styles.searchHeaderRow}>
                <ActivityIndicator size="small" color="#0E7C6B" style={styles.searchSpinner} />
              </View>
            )}

            {/* Search Input */}
            <TextInput
              style={styles.input}
              placeholder="Search by booking ID, name, email, or phone..."
              placeholderTextColor="#AEB8B6"
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
            />

            {searchQuery.length > 0 && searchQuery.length < 3 && (
              <Text style={styles.searchHint}>
                Type at least 3 characters to search...
              </Text>
            )}
            {searchQuery.length === 0 && (
              <Text style={styles.searchHint}>
                Showing all bookings. Type 3+ characters to search all fields.
              </Text>
            )}
          </View>
        ) : (
          <View style={styles.section}>
            <View style={styles.searchHeaderRow}>
              <Text style={styles.sectionTitle}>Search Database</Text>
              {searching && (
                <ActivityIndicator size="small" color="#0E7C6B" style={styles.searchSpinner} />
              )}
            </View>

            <TextInput
              style={styles.input}
              placeholder="Last Name"
              placeholderTextColor="#AEB8B6"
              value={searchLastName}
              onChangeText={(value) => {
                setSearchLastName(value);
                setHasDatabaseSearchRun(false);
              }}
              autoCapitalize="none"
            />

            <TextInput
              style={styles.input}
              placeholder="Phone Number"
              placeholderTextColor="#AEB8B6"
              value={searchPhone}
              keyboardType="phone-pad"
              onChangeText={(value) => {
                setSearchPhone(formatPhoneWithDashes(value));
                setHasDatabaseSearchRun(false);
              }}
              autoCapitalize="none"
            />

            <TextInput
              style={styles.input}
              placeholder="Email"
              placeholderTextColor="#AEB8B6"
              value={searchEmail}
              onChangeText={(value) => {
                setSearchEmail(value);
                setHasDatabaseSearchRun(false);
              }}
              autoCapitalize="none"
            />

            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.searchButton]}
              onPress={runCloudDatabaseSearch}
              disabled={searching}
            >
              {searching ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.buttonText}>Search Database</Text>
              )}
            </TouchableOpacity>

            <Text style={styles.searchHint}>
              Searches cloud Firestore in both active and archived bookings.
            </Text>
          </View>
        )}

        {/* Overdue Bookings Container - Above Current Bookings */}
        {shouldSplitOverdue && overdueBookings.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.overdueSectionTitle}>
              ⚠️ Overdue Bookings ({overdueBookings.length})
            </Text>
            {overdueBookings
              .sort((a, b) => {
                // Sort by createdAt, newest first
                const aTime = a.createdAt?.seconds || 0;
                const bTime = b.createdAt?.seconds || 0;
                return bTime - aTime;
              })
              .map((booking) => {
              const bookingId = booking.id || booking.bookingId;
              const bookingKey = `${booking.__sourceNode || "bookings"}:${bookingId}`;
              const totals = calculateBookingTotals(booking);

              // Get card info (color, border, type) based on services selected
              const cardInfo = getBookingCardInfo(booking);
              
              // Get start/end timestamps
              const startMillis = parseFirestoreTimestamp(booking.startDate);
              const endMillis = parseFirestoreTimestamp(booking.endDate);
              
              // Format customer name and phone
              const customerName = `${booking.firstName || ""} ${booking.lastName || ""}`.trim() || "Unknown";
              const capitalizedName = capitalizeWords(customerName);
              const formattedPhone = formatPhoneNumber(booking.customerPhone || "");
              
              // Format dates (like ViewBooking)
              const formattedStartDate = formatDateLong(booking.startDate);
              const formattedEndDate = formatDateLong(booking.endDate);
              
              // Get miles for delivery or pickup
              const hasDelivery = booking.delivery || false;
              const hasHomePickup = booking.homePickup || false;
              const showMiles = hasDelivery || hasHomePickup;
              const numMiles = booking.numMiles !== undefined && booking.numMiles !== null 
                ? parseFloat(Number(booking.numMiles).toFixed(1)) 
                : null;
              const milesText = showMiles && numMiles !== null && numMiles > 0 ? `${numMiles} mi` : null;
              
              // Format address for display (Street on line 1, City on line 2, no state/zip)
              let displayStreet = null;
              let displayCity = null;
              if (booking.deliveryAddress) {
                const addr = booking.deliveryAddress;
                if (typeof addr === "object") {
                  displayStreet = addr.address || null;
                  displayCity = addr.city || null;
                } else if (typeof addr === "string") {
                  const parts = addr.split(",").map(p => p.trim());
                  if (parts.length >= 3) {
                    displayStreet = parts[0];
                    displayCity = parts[1];
                  } else if (parts.length === 2) {
                    displayStreet = parts[0];
                    displayCity = parts[1].replace(/\s+\d{5}(-\d{4})?$/, "");
                  } else {
                    displayStreet = addr;
                  }
                }
              }
              const hasAddress = displayStreet || displayCity;
              
              // Get product items
              const product1Items = booking.product1 || [];
              const product2Items = booking.product2 || [];
              
                // Check if booking is cancelled or completed
                const isCancelled = booking.cancelled === true;
                const isArchivedCompleted = booking.__fromCompletedArchive === true;
                const isCompleted = isArchivedCompleted || completedBookings.some(cb => (cb.id === bookingId || cb.bookingId === bookingId) && !cb.cancelled);
                const isInCompletedBookings = isArchivedCompleted || isBookingInCompletedBookings(booking);

              return (
                <TouchableOpacity
                  key={bookingKey}
                  style={[styles.rentalCard, { borderColor: cardInfo.borderColor, borderWidth: 2 }]}
                  onPress={() => handleViewBooking(booking)}
                  activeOpacity={0.8}
                >
                  {/* Booking Status Badge - Complete or Cancelled */}
                  {isCancelled && (
                    <View style={styles.statusBadgeCancelled}>
                      <Text style={styles.statusBadgeTextCancelled}>Booking Cancelled</Text>
                    </View>
                  )}
                  {!isCancelled && isCompleted && (
                    <View style={styles.statusBadgeComplete}>
                      <Text style={styles.statusBadgeTextComplete}>Booking Complete</Text>
                    </View>
                  )}
                  {isArchivedCompleted && (
                    <View style={styles.statusBadgeArchived}>
                      <Text style={styles.statusBadgeTextArchived}>COMPLETED AND ARCHIVED</Text>
                    </View>
                  )}

                  {/* Badge */}
                  <View style={[styles.rentalBadge, { backgroundColor: cardInfo.color }]}>
                    <Text style={styles.rentalBadgeText}>
                      {cardInfo.type}
                    </Text>
                  </View>

                  {/* Flagged for Review Indicator */}
                  {booking.bookingIntake?.flaggedForReview && (
                    <View style={styles.flaggedIndicator}>
                      <Text style={styles.flaggedIcon}>🚩</Text>
                      <Text style={styles.flaggedText}>Check-in Flagged</Text>
                    </View>
                  )}

                  {/* Breakdown Reports Indicator */}
                  {(() => {
                    const breakdownReports = booking?.breakdownReports || [];
                    const incompleteReports = breakdownReports.filter(report => !report.completed);

                    if (incompleteReports.length > 0) {
                      // Check if any incomplete report is a roadside breakdown
                      const hasRoadsideBreakdown = incompleteReports.some(report => report.roadsideBreakdown === true);
                      // Check if any report doesn't have the flag (problem report)
                      const hasProblemReport = incompleteReports.some(report => report.roadsideBreakdown !== true);

                      // Determine label
                      let label = "Breakdown Reported!";
                      if (hasProblemReport && !hasRoadsideBreakdown) {
                        label = "Problem Reported!";
                      } else if (hasRoadsideBreakdown && !hasProblemReport) {
                        label = "Breakdown Reported!";
                      }

                      return (
                        <TouchableOpacity
                          style={styles.breakdownIndicator}
                          onPress={(e) => { e.stopPropagation?.(); navigate(`/problem-reports/${bookingId}`); }}
                        >
                          <Text style={styles.breakdownIcon}>⚠️</Text>
                          <Animated.Text style={[styles.breakdownText, { opacity: breakdownBlinkOpacity }]}>
                            {label}
                          </Animated.Text>
                        </TouchableOpacity>
                      );
                    }
                    return null;
                  })()}

                  <View style={styles.rentalInfo}>
                    {/* Booking ID */}
                    {booking.customerBookingID && (
                      <Text style={styles.bookingIdText}>
                        Booking ID: {booking.customerBookingID}
                      </Text>
                    )}
                    
                    {/* Name and Phone */}
                    <View style={styles.namePhoneRow}>
                      <Text style={styles.customerName}>
                        {capitalizedName}
                      </Text>
                      {formattedPhone && (
                        <TouchableOpacity
                          onPress={(e) => {
                            e.stopPropagation?.();
                            if (!isMobileDevice) {
                              navigate('/messages', {
                                state: {
                                  customer: {
                                    id: booking.customerPhone,
                                    phone: booking.customerPhone,
                                    fullName: capitalizedName,
                                    firstName: booking.firstName || '',
                                    lastName: booking.lastName || '',
                                    email: booking.customerEmail || '',
                                    bookingId: booking.id || booking.bookingId,
                                  }
                                }
                              });
                            } else {
                              setSelectedCustomer({
                                phone: booking.customerPhone,
                                fullName: capitalizedName,
                                firstName: booking.firstName,
                                lastName: booking.lastName,
                                email: booking.customerEmail,
                                bookingId: booking.id || booking.bookingId
                              });
                              setContactModalVisible(true);
                            }
                          }}
                        >
                          <Text style={[styles.rentalDetails, { color: '#0E7C6B' }]}>
                            {formattedPhone}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    
                    {/* Email */}
                    {booking.customerEmail && (
                      <View style={styles.emailRow}>
                        <TouchableOpacity
                          onPress={(e) => {
                            e.stopPropagation();
                            navigate('/gmail-inbox', {
                              state: {
                                customerEmail: booking.customerEmail,
                                composeTo: booking.customerEmail,
                                composeSubject: `${shopInfo?.name || "Bonita Bikes"} rental communication`,
                                customerName: capitalizedName,
                                storeName: shopInfo?.name || "Bonita Bikes"
                              }
                            });
                          }}
                        >
                          <Text style={[styles.rentalDetails, { color: '#0E7C6B', textDecorationLine: 'underline' }]}>
                            {booking.customerEmail}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    )}
                    
                    {/* Address - Right side, under phone */}
                    {hasAddress && (
                      <View style={styles.addressRow}>
                        <View style={{ flex: 1 }} />
                        <TouchableOpacity 
                          style={styles.addressLinkContainer}
                          onPress={(e) => {
                            e.stopPropagation();
                            navigate("/map-viewer", { 
                              state: { 
                                address: booking.deliveryAddress,
                                customerName: capitalizedName
                              } 
                            });
                          }}
                        >
                          {displayStreet && <Text style={styles.addressLinkText}>📍 {displayStreet}</Text>}
                          {displayCity && <Text style={styles.addressLinkText}>{displayStreet ? "    " : "📍 "}{displayCity}</Text>}
                        </TouchableOpacity>
                      </View>
                    )}
                    
                    {/* Start Date */}
                    <View style={styles.dateRow}>
                      <Text style={styles.dateLabel}>Start:</Text>
                      <Text style={[styles.rentalDetails, { color: "#1F8A5B", fontWeight: "500" }]}>
                        {formattedStartDate}
                      </Text>
                    </View>
                    
                    {/* End Date */}
                    <View style={styles.dateRow}>
                      <Text style={styles.dateLabel}>End:</Text>
                      <Text style={[styles.rentalDetails, { color: "#C4453D", fontWeight: "500" }]}>
                        {formattedEndDate}
                      </Text>
                    </View>
                    
                    {/* Miles (if applicable) */}
                    {milesText && (
                      <View style={styles.dateRow}>
                        <Text style={styles.dateLabel}>Distance:</Text>
                        <Text style={styles.rentalDetails}>
                          {milesText}
                        </Text>
                      </View>
                    )}
                    
                    {/* Products */}
                    <View style={styles.productsContainer}>
                      {product1Items.map((product, idx) => (
                        <Text key={`p1-${idx}`} style={[styles.rentalDetails, styles.product1Item]}>
                          {product.quantity || 0}x {product.name || "Unknown"}
                        </Text>
                      ))}
                      {product2Items.map((product, idx) => (
                        <Text key={`p2-${idx}`} style={[styles.rentalDetails, styles.product2Item]}>
                          {product.quantity || 0}x {product.name || "Unknown"}
                        </Text>
                      ))}
                    </View>

                    {/* Total and Paid */}
                    <View style={[styles.namePhoneRow, { marginTop: 4 }]}>
                       <Text style={[styles.rentalDetails, { fontWeight: 'bold' }]}>
                        Booking Total: {formatCurrency(booking.totalAmount || 0)}
                       </Text>
                       {totals.totalPaid > 0 ? (
                         <Text style={[styles.rentalDetails, { fontWeight: 'bold', color: '#1F8A5B' }]}>
                           Paid: {formatCurrency(totals.totalPaid)}
                         </Text>
                       ) : (
                         <Animated.Text style={[styles.rentalDetails, { fontWeight: 'bold', color: '#C4453D', opacity: paidBlinkOpacity }]}>
                           Paid: {formatCurrency(totals.totalPaid)}
                         </Animated.Text>
                       )}
                    </View>
                  </View>

                  {/* Sign Waiver / Send Waiver Buttons - Show if no original booker waiver AND not in completedBookings */}
                  {(() => {
                    // Don't show waiver buttons if booking is in completedBookings
                    if (isInCompletedBookings) return null;

                    // Check if waiver exists - check waiverData array first, fallback to waivers for legacy
                    // Also check waiverSigned boolean
                    const hasOriginalBookerWaiver = booking.waiverSigned || 
                      (booking.waiverData && booking.waiverData.some(w => w.isOriginalBooker === true)) ||
                      (booking.waivers && booking.waivers.some(w => w.isOriginalBooker === true));
                    
                    if (hasOriginalBookerWaiver) return null;
                    
                    // Check if has bicycles (product1 items)
                    const hasBicycles = product1Items && product1Items.length > 0 && product1Items.some(p => (p.quantity || 0) > 0);
                    
                    return (
                      <View style={styles.paymentButtonsRow}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[styles.paymentButton, styles.signWaiverButton]}
                          onPress={(e) => {
                            e.stopPropagation();
                            navigate("/waiver", {
                              state: {
                                customerName: capitalizedName,
                                hasBicycles: hasBicycles,
                                bookingId: booking.id || booking.bookingId,
                                isOriginalBooker: true,
                              },
                            });
                          }}
                        >
                          <Text style={styles.buttonText}>📝 Sign Waiver</Text>
                        </TouchableOpacity>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[styles.paymentButton, styles.sendWaiverButton]}
                          onPress={(e) => {
                            e.stopPropagation();
                            handleSendWaiver(booking);
                          }}
                          disabled={sendingWaiver}
                        >
                          {sendingWaiver ? (
                            <ActivityIndicator color="#2E3A38" size="small" />
                          ) : (
                            <Text style={styles.buttonText}>📧 Send Waiver</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    );
                  })()}

                  {/* Admin notes - above button row */}
                  {booking.adminNotes?.trim() && (
                    <View style={styles.adminNotesCard}>
                      <Text style={styles.adminNotesLabel}>Admin  Notes:</Text>
                      <Text style={styles.adminNotesText}>{booking.adminNotes.trim()}</Text>
                    </View>
                  )}

                  {/* Actions */}
                  <View style={styles.actionButtonsDivider} />
                  
                  {/* Payment Collection Buttons - Only show if no payments AND not in completedBookings */}
                  {!totals.hasPaymentData && !isInCompletedBookings && (
                    <View style={styles.paymentButtonsRow}>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[styles.paymentButton, styles.collectPaymentButton]}
                        onPress={(e) => { e.stopPropagation?.(); handleCollectPayment(booking); }}
                      >
                        <Text style={styles.buttonText}>💳 Collect Payment</Text>
                      </TouchableOpacity>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[styles.paymentButton, styles.sendInvoiceButton]}
                        onPress={(e) => { e.stopPropagation?.(); handleSendTextInvoice(booking); }}
                      >
                        <Text style={styles.buttonText}>📱 Send Text Invoice</Text>
                      </TouchableOpacity>
                    </View>
                  )}

                  <View style={[styles.summaryActions, { padding: 12, marginTop: 0 }]}>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[
                        styles.button,
                        styles.editButton,
                        { marginTop: 0, opacity: isInCompletedBookings ? 0.5 : 1 }
                      ]}
                      onPress={(e) => { e.stopPropagation?.(); handleEditBooking(booking); }}
                      disabled={isInCompletedBookings}
                    >
                      <Text style={styles.buttonText}>✏️ Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[
                        styles.button,
                        { flex: 1, backgroundColor: "#1F8A5B", marginTop: 0, opacity: isInCompletedBookings ? 0.5 : 1 }
                      ]}
                      onPress={(e) => {
                        e.stopPropagation?.();
                        const bookingIdForCheckIn = booking.id || booking.bookingId;
                        navigate(`/check-in/${bookingIdForCheckIn}`);
                      }}
                      disabled={isInCompletedBookings}
                    >
                      <Text style={styles.buttonText}>✓ Check In</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        {/* Bookings List - All or Filtered */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            {isLocalSearchActive || isCloudSearchActive
              ? `Found ${currentBookings.length} booking(s)`
              : `${activeTabLabel} Bookings (${currentBookings.length})`
            }
          </Text>
          <View style={styles.sortContainer}>
            <Text style={styles.sortLabel}>Sort list by date:</Text>
            <View style={styles.sortButtonsRow}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.sortButton, listSortMode === "startEarliest" && styles.sortButtonActive]}
                onPress={() => setListSortMode("startEarliest")}
              >
                <Text style={[styles.sortButtonText, listSortMode === "startEarliest" && styles.sortButtonTextActive]}>
                  Start Earliest
                </Text>
              </TouchableOpacity>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.sortButton, listSortMode === "startLatest" && styles.sortButtonActive]}
                onPress={() => setListSortMode("startLatest")}
              >
                <Text style={[styles.sortButtonText, listSortMode === "startLatest" && styles.sortButtonTextActive]}>
                  Start Latest
                </Text>
              </TouchableOpacity>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.sortButton, listSortMode === "endEarliest" && styles.sortButtonActive]}
                onPress={() => setListSortMode("endEarliest")}
              >
                <Text style={[styles.sortButtonText, listSortMode === "endEarliest" && styles.sortButtonTextActive]}>
                  End Earliest
                </Text>
              </TouchableOpacity>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.sortButton, listSortMode === "endLatest" && styles.sortButtonActive]}
                onPress={() => setListSortMode("endLatest")}
              >
                <Text style={[styles.sortButtonText, listSortMode === "endLatest" && styles.sortButtonTextActive]}>
                  End Latest
                </Text>
              </TouchableOpacity>
            </View>
          </View>
          {currentBookings.length === 0 && (
            <Text style={styles.noResultsText}>
              {isCloudSearchTab
                ? (isCloudSearchActive
                  ? "No bookings found in cloud database"
                  : "Enter last name, phone number, or email, then tap Search Database")
                : (isLocalSearchActive
                  ? "No bookings match your search"
                  : "No bookings available")
              }
            </Text>
          )}
          {sortedCurrentBookings.map((booking) => {
              // NEW STRUCTURE: Flat booking, no sub-bookings
              const bookingId = booking.id || booking.bookingId;
              const bookingKey = `${booking.__sourceNode || "bookings"}:${bookingId}`;
              const totals = calculateBookingTotals(booking);

              // Get card info (color, border, type) based on services selected
              const cardInfo = getBookingCardInfo(booking);
              
              // Get start/end timestamps
              const startMillis = parseFirestoreTimestamp(booking.startDate);
              const endMillis = parseFirestoreTimestamp(booking.endDate);
              
              // Format customer name and phone
              const customerName = `${booking.firstName || ""} ${booking.lastName || ""}`.trim() || "Unknown";
              const capitalizedName = capitalizeWords(customerName);
              const formattedPhone = formatPhoneNumber(booking.customerPhone || "");
              
              // Format dates (like ViewBooking)
              const formattedStartDate = formatDateLong(booking.startDate);
              const formattedEndDate = formatDateLong(booking.endDate);
              
              // Get miles for delivery or pickup
              const hasDelivery = booking.delivery || false;
              const hasHomePickup = booking.homePickup || false;
              const showMiles = hasDelivery || hasHomePickup;
              const numMiles = booking.numMiles !== undefined && booking.numMiles !== null 
                ? parseFloat(Number(booking.numMiles).toFixed(1)) 
                : null;
              const milesText = showMiles && numMiles !== null && numMiles > 0 ? `${numMiles} mi` : null;
              
              // Format address for display (Street on line 1, City on line 2, no state/zip)
              let displayStreet = null;
              let displayCity = null;
              if (booking.deliveryAddress) {
                const addr = booking.deliveryAddress;
                if (typeof addr === "object") {
                  displayStreet = addr.address || null;
                  displayCity = addr.city || null;
                } else if (typeof addr === "string") {
                  const parts = addr.split(",").map(p => p.trim());
                  if (parts.length >= 3) {
                    displayStreet = parts[0];
                    displayCity = parts[1];
                  } else if (parts.length === 2) {
                    displayStreet = parts[0];
                    displayCity = parts[1].replace(/\s+\d{5}(-\d{4})?$/, "");
                  } else {
                    displayStreet = addr;
                  }
                }
              }
              const hasAddress = displayStreet || displayCity;
              
              // Get product items
              const product1Items = booking.product1 || [];
              const product2Items = booking.product2 || [];
              
              // Check if booking is cancelled or completed
              const isCancelled = booking.cancelled === true;
              const isArchivedCompleted = booking.__fromCompletedArchive === true;
              const isCompleted = isArchivedCompleted || completedBookings.some(cb => (cb.id === bookingId || cb.bookingId === bookingId) && !cb.cancelled);
              const isInCompletedBookings = isArchivedCompleted || isBookingInCompletedBookings(booking);

              return (
                <TouchableOpacity
                  key={bookingKey}
                  style={[styles.rentalCard, { borderColor: cardInfo.borderColor, borderWidth: 2 }]}
                  onPress={() => handleViewBooking(booking)}
                  activeOpacity={0.8}
                >
                  {/* Booking Status Badge - Complete or Cancelled */}
                  {isCancelled && (
                    <View style={styles.statusBadgeCancelled}>
                      <Text style={styles.statusBadgeTextCancelled}>Booking Cancelled</Text>
                    </View>
                  )}
                  {!isCancelled && isCompleted && (
                    <View style={styles.statusBadgeComplete}>
                      <Text style={styles.statusBadgeTextComplete}>Booking Complete</Text>
                    </View>
                  )}
                  {isArchivedCompleted && (
                    <View style={styles.statusBadgeArchived}>
                      <Text style={styles.statusBadgeTextArchived}>COMPLETED AND ARCHIVED</Text>
                    </View>
                  )}

                  {/* Badge */}
                  <View style={[styles.rentalBadge, { backgroundColor: cardInfo.color }]}>
                    <Text style={styles.rentalBadgeText}>
                      {cardInfo.type}
                    </Text>
                  </View>

                  {/* Flagged for Review Indicator */}
                  {booking.bookingIntake?.flaggedForReview && (
                    <View style={styles.flaggedIndicator}>
                      <Text style={styles.flaggedIcon}>🚩</Text>
                      <Text style={styles.flaggedText}>Check-in Flagged</Text>
                    </View>
                  )}

                  {/* Breakdown Reports Indicator */}
                  {(() => {
                    const breakdownReports = booking?.breakdownReports || [];
                    const incompleteReports = breakdownReports.filter(report => !report.completed);

                    if (incompleteReports.length > 0) {
                      // Check if any incomplete report is a roadside breakdown
                      const hasRoadsideBreakdown = incompleteReports.some(report => report.roadsideBreakdown === true);
                      // Check if any report doesn't have the flag (problem report)
                      const hasProblemReport = incompleteReports.some(report => report.roadsideBreakdown !== true);

                      // Determine label
                      let label = "Breakdown Reported!";
                      if (hasProblemReport && !hasRoadsideBreakdown) {
                        label = "Problem Reported!";
                      } else if (hasRoadsideBreakdown && !hasProblemReport) {
                        label = "Breakdown Reported!";
                      }

                      return (
                        <TouchableOpacity
                          style={styles.breakdownIndicator}
                          onPress={(e) => { e.stopPropagation?.(); navigate(`/problem-reports/${bookingId}`); }}
                        >
                          <Text style={styles.breakdownIcon}>⚠️</Text>
                          <Animated.Text style={[styles.breakdownText, { opacity: breakdownBlinkOpacity }]}>
                            {label}
                          </Animated.Text>
                        </TouchableOpacity>
                      );
                    }
                    return null;
                  })()}

                  <View style={styles.rentalInfo}>
                    {/* Booking ID */}
                    {booking.customerBookingID && (
                      <Text style={styles.bookingIdText}>
                        Booking ID: {booking.customerBookingID}
                      </Text>
                    )}
                    
                    {/* Name and Phone */}
                    <View style={styles.namePhoneRow}>
                      <Text style={styles.customerName}>
                        {capitalizedName}
                      </Text>
                      {formattedPhone && (
                        <TouchableOpacity
                          onPress={(e) => {
                            e.stopPropagation?.();
                            if (!isMobileDevice) {
                              navigate('/messages', {
                                state: {
                                  customer: {
                                    id: booking.customerPhone,
                                    phone: booking.customerPhone,
                                    fullName: capitalizedName,
                                    firstName: booking.firstName || '',
                                    lastName: booking.lastName || '',
                                    email: booking.customerEmail || '',
                                    bookingId: booking.id || booking.bookingId,
                                  }
                                }
                              });
                            } else {
                              setSelectedCustomer({
                                phone: booking.customerPhone,
                                fullName: capitalizedName,
                                firstName: booking.firstName,
                                lastName: booking.lastName,
                                email: booking.customerEmail,
                                bookingId: booking.id || booking.bookingId
                              });
                              setContactModalVisible(true);
                            }
                          }}
                        >
                          <Text style={[styles.rentalDetails, { color: '#0E7C6B' }]}>
                            {formattedPhone}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    
                    {/* Email */}
                    {booking.customerEmail && (
                      <View style={styles.emailRow}>
                        <TouchableOpacity
                          onPress={(e) => {
                            e.stopPropagation();
                            navigate('/gmail-inbox', {
                              state: {
                                customerEmail: booking.customerEmail,
                                composeTo: booking.customerEmail,
                                composeSubject: `${shopInfo?.name || "Bonita Bikes"} rental communication`,
                                customerName: capitalizedName,
                                storeName: shopInfo?.name || "Bonita Bikes"
                              }
                            });
                          }}
                        >
                          <Text style={[styles.rentalDetails, { color: '#0E7C6B', textDecorationLine: 'underline' }]}>
                            {booking.customerEmail}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    )}
                    
                    {/* Address - Right side, under phone */}
                    {hasAddress && (
                      <View style={styles.addressRow}>
                        <View style={{ flex: 1 }} />
                        <TouchableOpacity 
                          style={styles.addressLinkContainer}
                          onPress={(e) => {
                            e.stopPropagation();
                            navigate("/map-viewer", { 
                              state: { 
                                address: booking.deliveryAddress,
                                customerName: capitalizedName
                              } 
                            });
                          }}
                        >
                          {displayStreet && <Text style={styles.addressLinkText}>📍 {displayStreet}</Text>}
                          {displayCity && <Text style={styles.addressLinkText}>{displayStreet ? "    " : "📍 "}{displayCity}</Text>}
                        </TouchableOpacity>
                      </View>
                    )}
                    
                    {/* Start Date */}
                    <View style={styles.dateRow}>
                      <Text style={styles.dateLabel}>Start:</Text>
                      <Text style={[styles.rentalDetails, { color: "#1F8A5B", fontWeight: "500" }]}>
                        {formattedStartDate}
                      </Text>
                    </View>
                    
                    {/* End Date */}
                    <View style={styles.dateRow}>
                      <Text style={styles.dateLabel}>End:</Text>
                      <Text style={[styles.rentalDetails, { color: "#C4453D", fontWeight: "500" }]}>
                        {formattedEndDate}
                      </Text>
                    </View>
                    
                    {/* Miles (if applicable) */}
                    {milesText && (
                      <View style={styles.dateRow}>
                        <Text style={styles.dateLabel}>Distance:</Text>
                        <Text style={styles.rentalDetails}>
                          {milesText}
                        </Text>
                      </View>
                    )}
                    
                    {/* Products */}
                    <View style={styles.productsContainer}>
                      {product1Items.map((product, idx) => (
                        <Text key={`p1-${idx}`} style={[styles.rentalDetails, styles.product1Item]}>
                          {product.quantity || 0}x {product.name || "Unknown"}
                        </Text>
                      ))}
                      {product2Items.map((product, idx) => (
                        <Text key={`p2-${idx}`} style={[styles.rentalDetails, styles.product2Item]}>
                          {product.quantity || 0}x {product.name || "Unknown"}
                        </Text>
                      ))}
                    </View>

                    {/* Total and Paid */}
                    <View style={[styles.namePhoneRow, { marginTop: 4 }]}>
                       <Text style={[styles.rentalDetails, { fontWeight: 'bold' }]}>
                        Booking Total: {formatCurrency(booking.totalAmount || 0)}
                       </Text>
                       {totals.totalPaid > 0 ? (
                         <Text style={[styles.rentalDetails, { fontWeight: 'bold', color: '#1F8A5B' }]}>
                           Paid: {formatCurrency(totals.totalPaid)}
                         </Text>
                       ) : (
                         <Animated.Text style={[styles.rentalDetails, { fontWeight: 'bold', color: '#C4453D', opacity: paidBlinkOpacity }]}>
                           Paid: {formatCurrency(totals.totalPaid)}
                         </Animated.Text>
                       )}
                    </View>
                  </View>

                  {/* Sign Waiver / Send Waiver Buttons - Show if no original booker waiver AND not in completedBookings */}
                  {(() => {
                    // Don't show waiver buttons if booking is in completedBookings
                    if (isInCompletedBookings) return null;

                    // Check if waiver exists - check waiverData array first, fallback to waivers for legacy
                    // Also check waiverSigned boolean
                    const hasOriginalBookerWaiver = booking.waiverSigned || 
                      (booking.waiverData && booking.waiverData.some(w => w.isOriginalBooker === true)) ||
                      (booking.waivers && booking.waivers.some(w => w.isOriginalBooker === true));
                    
                    if (hasOriginalBookerWaiver) return null;
                    
                    // Check if has bicycles (product1 items)
                    const hasBicycles = product1Items && product1Items.length > 0 && product1Items.some(p => (p.quantity || 0) > 0);
                    
                    return (
                      <View style={styles.paymentButtonsRow}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[styles.paymentButton, styles.signWaiverButton]}
                          onPress={(e) => {
                            e.stopPropagation();
                            navigate("/waiver", {
                              state: {
                                customerName: capitalizedName,
                                hasBicycles: hasBicycles,
                                bookingId: booking.id || booking.bookingId,
                                isOriginalBooker: true,
                              },
                            });
                          }}
                        >
                          <Text style={styles.buttonText}>📝 Sign Waiver</Text>
                        </TouchableOpacity>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[styles.paymentButton, styles.sendWaiverButton]}
                          onPress={(e) => {
                            e.stopPropagation();
                            handleSendWaiver(booking);
                          }}
                          disabled={sendingWaiver}
                        >
                          {sendingWaiver ? (
                            <ActivityIndicator color="#2E3A38" size="small" />
                          ) : (
                            <Text style={styles.buttonText}>📧 Send Waiver</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    );
                  })()}

                  {/* Admin notes - above button row */}
                  {booking.adminNotes?.trim() && (
                    <View style={styles.adminNotesCard}>
                      <Text style={styles.adminNotesLabel}>Admin  Notes:</Text>
                      <Text style={styles.adminNotesText}>{booking.adminNotes.trim()}</Text>
                    </View>
                  )}

                  {/* Actions */}
                  <View style={styles.actionButtonsDivider} />
                  
                  {/* Payment Collection Buttons - Only show if no payments AND not in completedBookings */}
                  {!totals.hasPaymentData && !isInCompletedBookings && (
                    <View style={styles.paymentButtonsRow}>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[styles.paymentButton, styles.collectPaymentButton]}
                        onPress={(e) => { e.stopPropagation?.(); handleCollectPayment(booking); }}
                      >
                        <Text style={styles.buttonText}>💳 Collect Payment</Text>
                      </TouchableOpacity>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[styles.paymentButton, styles.sendInvoiceButton]}
                        onPress={(e) => { e.stopPropagation?.(); handleSendTextInvoice(booking); }}
                      >
                        <Text style={styles.buttonText}>📱 Send Text Invoice</Text>
                      </TouchableOpacity>
                    </View>
                  )}

                  <View style={[styles.summaryActions, { padding: 12, marginTop: 0 }]}>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[
                        styles.button,
                        styles.editButton,
                        { marginTop: 0, opacity: isInCompletedBookings ? 0.5 : 1 }
                      ]}
                      onPress={(e) => { e.stopPropagation?.(); handleEditBooking(booking); }}
                      disabled={isInCompletedBookings}
                    >
                      <Text style={styles.buttonText}>✏️ Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[
                        styles.button,
                        { flex: 1, backgroundColor: "#1F8A5B", marginTop: 0, opacity: isInCompletedBookings ? 0.5 : 1 }
                      ]}
                      onPress={(e) => {
                        e.stopPropagation?.();
                        const bookingIdForCheckIn = booking.id || booking.bookingId;
                        navigate(`/check-in/${bookingIdForCheckIn}`);
                      }}
                      disabled={isInCompletedBookings}
                    >
                      <Text style={styles.buttonText}>✓ Check In</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              );
            })}
        </View>

      </ScrollView>
      </View>
      
      {/* Loading Modal for Send Invoice */}
      <Modal
        visible={sendingInvoice}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.modalText}>Sending invoice...</Text>
          </View>
        </View>
      </Modal>
      
      {/* Loading Modal for Collect Payment */}
      <Modal
        visible={collectingPayment}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.modalText}>Creating payment link...</Text>
          </View>
        </View>
      </Modal>

      {/* Loading Modal for Send Waiver */}
      <Modal
        visible={sendingWaiver}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.modalText}>Sending waiver reminder...</Text>
          </View>
        </View>
      </Modal>

      {/* Searching Modal - Blocks UI while searching */}
      <Modal
        visible={searching}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.modalText}>Searching...</Text>
          </View>
        </View>
      </Modal>

      {/* Contact Options Modal */}
      <ContactOptionsModal
        visible={contactModalVisible}
        onClose={() => setContactModalVisible(false)}
        customer={selectedCustomer}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  content: {
    flex: 1,
  },
  section: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  searchSection: {
    backgroundColor: "#F6F8F7",
    marginHorizontal: 16,
    marginVertical: 12,
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
  },
  searchHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  searchSpinner: {
    marginLeft: 10,
  },
  overdueSectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
    color: "#C4453D",
  },
  checkInIndicatorGreen: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#1F8A5B",
    borderWidth: 1,
    borderColor: "#FFFFFF",
  },
  checkInIndicatorRed: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#C4453D",
    borderWidth: 1,
    borderColor: "#FFFFFF",
  },
  noResultsText: {
    textAlign: "center",
    fontSize: 14,
    color: "#8A9794",
    padding: 20,
    fontStyle: "italic",
  },
  searchTypeRow: {
    flexDirection: "row",
    marginBottom: 12,
  },
  searchTypeButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "#EDF1F0",
    alignItems: "center",
  },
  searchTypeButtonActive: {
    backgroundColor: "#0E7C6B",
  },
  searchTypeText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
    textAlign: "center",
  },
  searchTypeTextActive: {
    color: "#FFFFFF",
    textAlign: "center",
  },
  input: {
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 16,
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
  },
  multilineInput: {
    height: 80,
    textAlignVertical: "top",
    paddingTop: 12,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
    marginTop: 8,
  },
  button: {
    height: 44,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
  },
  searchButton: {
    backgroundColor: "#0E7C6B",
  },
  summaryActions: {
    flexDirection: "row",
    marginTop: 12,
    gap: 12,
  },
  paymentButtonsRow: {
    flexDirection: "row",
    marginTop: 12,
    marginBottom: 12,
    paddingHorizontal: 12,
    gap: 12,
    width: "100%",
  },
  paymentButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  collectPaymentButton: {
    backgroundColor: "#C4453D", // Red
  },
  sendInvoiceButton: {
    backgroundColor: "#0E7C6B", // Magenta
  },
  viewButton: {
    flex: 1,
    backgroundColor: "#B4761B",
    marginRight: 8,
  },
  editButton: {
    flex: 1,
    backgroundColor: "#0E7C6B",
    marginRight: 8,
  },
  refundButton: {
    flex: 1,
    backgroundColor: "#C4453D",
  },
  processRefundButton: {
    backgroundColor: "#C4453D",
    marginTop: 16,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  bookingCard: {
    padding: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    marginBottom: 12,
    backgroundColor: "#F4F6F6",
  },
  bookingHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  bookingId: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
  },
  bookingDate: {
    fontSize: 12,
    color: "#5C6B68",
  },
  customerName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  customerContact: {
    fontSize: 13,
    color: "#5C6B68",
    marginBottom: 2,
  },
  bookingDates: {
    fontSize: 13,
    color: "#5C6B68",
    marginTop: 8,
    marginBottom: 4,
  },
  bookingCost: {
    fontSize: 14,
    color: "#0E7C6B",
    fontWeight: "600",
    marginBottom: 4,
  },
  bookingStatus: {
    fontSize: 13,
    fontWeight: "600",
    color: "#8A9794",
    marginBottom: 12,
  },
  paidStatus: {
    color: "#1F8A5B",
  },
  pendingStatus: {
    color: "#B4761B",
  },
  actionButtonsRow: {
    flexDirection: "row",
  },
  refundHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#EDF1F0",
    justifyContent: "center",
    alignItems: "center",
  },
  closeButtonText: {
    fontSize: 18,
    color: "#5C6B68",
    fontWeight: "600",
    textAlign: "center",
  },
  selectedBookingInfo: {
    padding: 12,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  selectedBookingText: {
    fontSize: 14,
    color: "#2E3A38",
    marginBottom: 4,
  },
  quickAmountRow: {
    flexDirection: "row",
    marginBottom: 12,
  },
  quickAmountButton: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 5,
    backgroundColor: "#EDF1F0",
    alignItems: "center",
  },
  quickAmountText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#0E7C6B",
  },
  refundNote: {
    fontSize: 12,
    color: "#5C6B68",
    marginTop: 12,
    fontStyle: "italic",
    textAlign: "center",
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    fontSize: 16,
    color: "#5C6B68",
    marginTop: 12,
  },
  masterBookingContainer: {
    marginBottom: 20,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    backgroundColor: "#E3F2EF",
    padding: 12,
  },
  masterBookingHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: "#0E7C6B",
  },
  masterBookingId: {
    fontSize: 16,
    fontWeight: "700",
    color: "#0E7C6B",
  },
  masterBookingVersions: {
    fontSize: 14,
    fontWeight: "600",
    color: "#0E7C6B",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 8,
  },
  subBookingCard: {
    backgroundColor: "#FFFFFF",
    padding: 14,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    marginBottom: 10,
  },
  infoSection: {
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: "#8A9794",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  productItem: {
    fontSize: 13,
    color: "#2E3A38",
    marginBottom: 2,
    paddingLeft: 4,
  },
  costDetail: {
    fontSize: 12,
    color: "#5C6B68",
    marginTop: 2,
  },
  bookingDays: {
    fontSize: 12,
    color: "#5C6B68",
    marginTop: 2,
  },
  searchHint: {
    fontSize: 12,
    color: "#8A9794",
    fontStyle: "italic",
    marginTop: 8,
  },
  summaryContainer: {
    marginTop: 0,
  },
  summaryTabBlock: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 10,
    backgroundColor: "#F4F6F6",
  },
  summaryTabTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: "#2E3A38",
    marginBottom: 6,
  },
  summaryLine: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
  },
  summaryItemsText: {
    marginTop: 4,
    fontSize: 13,
    color: "#5C6B68",
    lineHeight: 18,
  },
  sortContainer: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    backgroundColor: "#F4F6F6",
    padding: 10,
    marginBottom: 12,
  },
  sortLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 8,
  },
  sortButtonsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  sortButton: {
    backgroundColor: "#EDF1F0",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  sortButtonActive: {
    backgroundColor: "#0E7C6B",
    borderColor: "#0E7C6B",
  },
  sortButtonText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#5C6B68",
  },
  sortButtonTextActive: {
    color: "#FFFFFF",
  },
  // Master Booking Summary Card Styles
  masterSummaryCard: {
    backgroundColor: "#FFFFFF",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    marginBottom: 14,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 1,
  },
  summaryHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  },
  summaryHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  summaryBookingId: {
    fontSize: 14,
    fontWeight: "700",
    color: "#0E7C6B",
  },
  summaryVersions: {
    fontSize: 11,
    fontWeight: "600",
    color: "#0E7C6B",
    backgroundColor: "#E3F2EF",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  dateHeader: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  dateHeaderText: {
    fontSize: 16,
    fontWeight: "700",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  summaryLabel: {
    fontSize: 13,
    color: "#5C6B68",
    fontWeight: "500",
  },
  summaryValue: {
    fontSize: 13,
    color: "#2E3A38",
    fontWeight: "400",
    flex: 1,
    textAlign: "right",
  },
  summaryActions: {
    flexDirection: "row",
    marginTop: 14,
  },
  cancelButton: {
    backgroundColor: "#C4453D",
    marginLeft: 10,
  },
  // New Card Layout Styles matching Today.js
  rentalCard: {
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#F4F6F6",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    marginBottom: 10,
  },
  rentalBadge: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    alignItems: "center",
  },
  rentalBadgeText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  statusBadgeComplete: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#1F8A5B",
  },
  statusBadgeTextComplete: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  statusBadgeCancelled: {
    backgroundColor: "#0F1A19",
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#2E3A38",
  },
  statusBadgeTextCancelled: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  statusBadgeArchived: {
    backgroundColor: "#5C6B68",
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#5C6B68",
  },
  statusBadgeTextArchived: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "bold",
    letterSpacing: 0.5,
  },
  flaggedIndicator: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FDF3E3",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#FDF3E3",
  },
  flaggedIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  flaggedText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#7A4E12",
  },
  breakdownIndicator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#C4453D",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#C4453D",
  },
  breakdownIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  breakdownText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#B4761B",
  },
  rentalInfo: {
    padding: 10,
  },
  namePhoneRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  emailRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 6,
  },
  durationMilesRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  rentalDetails: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 4,
  },
  productsContainer: {
    alignItems: "center",
    marginTop: 6,
    marginBottom: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: "#EDF1F0",
  },
  product1Item: {
    color: "#0E7C6B",
    fontWeight: "500",
    fontSize: 14,
    marginBottom: 2,
  },
  product2Item: {
    color: "#1F8A5B",
    fontWeight: "500",
    fontSize: 14,
    marginBottom: 2,
  },
  actionButtonsDivider: {
    height: 1,
    backgroundColor: "#EDF1F0",
    marginHorizontal: 12,
  },
  adminNotesCard: {
    marginTop: 12,
    marginHorizontal: 12,
    marginBottom: 8,
    padding: 10,
    backgroundColor: "#FDF3E3",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#F4F6F6",
    borderLeftWidth: 3,
    borderLeftColor: "#B4761B",
  },
  adminNotesLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 4,
  },
  adminNotesText: {
    fontSize: 13,
    color: "#2E3A38",
    lineHeight: 18,
  },
  bookingIdText: {
    fontSize: 11,
    color: "#5C6B68",
    fontWeight: "600",
    marginBottom: 5,
  },
  addressRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 5,
  },
  addressLinkContainer: {
    flexShrink: 1,
  },
  addressLinkText: {
    fontSize: 13,
    color: "#0E7C6B",
    textAlign: "right",
  },
  dateRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  dateLabel: {
    fontSize: 13,
    color: "#5C6B68",
    fontWeight: "600",
    marginRight: 8,
    minWidth: 50,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    padding: 30,
    borderRadius: 8,
    alignItems: "center",
    minWidth: 200,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  modalText: {
    marginTop: 16,
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
  },
  signWaiverButton: {
    backgroundColor: '#B4761B', // Bright yellow
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  sendWaiverButton: {
    backgroundColor: '#B4761B', // Darker yellow
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  signWaiverButtonText: {
    color: '#0F1A19',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
    overflow: 'hidden',
  },
  tabButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F6F8F7',
    borderRightWidth: 1,
    borderRightColor: '#DDE3E2',
  },
  tabButtonActive: {
    backgroundColor: '#0E7C6B',
  },
  tabButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#5C6B68',
  },
  tabButtonTextActive: {
    color: '#FFFFFF',
  },
});

export default Admin;


